import { observable, reaction, runInAction, makeObservable } from 'mobx';
import { observer, useLocalStore } from 'mobx-react';
import { createContext, PropsWithChildren, useContext } from 'react';
import { SimpleRequestAsync } from '../../../../Framework/Helpers/ApiHelpers';
import BaseUrl from '../../../../Framework/Helpers/BaseUrl';
import ErrorHandlingStore from '../../../../Framework/React/MobX/ErrorHandlingStore';
import PasswordResult from './PasswordResult';

export default class Store extends ErrorHandlingStore {
    public password: string | undefined;

    public others: Map<string, string> = new Map();

    public result: PasswordResult | undefined;

    public loading = false;

    constructor() {
        super();

        makeObservable(this, {
            password: observable,
            others: observable,
            result: observable,
            loading: observable
        });

        const update = () => this.refreshAsync().catch(this.handleError);

        reaction(() => this.password, update, { delay: 250 });
        reaction(() => this.others, update, { delay: 250 });
    }

    refreshAsync = async () => {
        runInAction(() => {
            this.loading = true;
        });

        if (this.password && this.password.trim() !== '') {
            const others = Array.from(this.others.values());
            const result = await SimpleRequestAsync<PasswordResult>(
                `${BaseUrl()}password/validate`,
                'POST',
                true,
                {
                    password: this.password,
                    otherValues: others
                }
            );
            runInAction(() => {
                this.result = result;
            });
        } else {
            runInAction(() => {
                this.result = undefined;
            });
        }

        runInAction(() => {
            this.loading = false;
        });
    };

    setPassword = (password?: string) => {
        runInAction(() => {
            this.password = password;
        });
    };

    setOther = (key: string, value?: string) => {
        runInAction(() => {
            if (value) {
                this.others.set(key, value);
            } else {
                this.others.delete(key);
            }
        });
    };
}

export const storesContext = createContext<Store | null>(null);

export const useStore = () => {
    const store = useContext(storesContext);
    if (!store) {
        throw new Error('useStore must be used within a StoreProvider.');
    }
    return store;
};

export const ThrowStoreError = observer(() => {
    const store = useStore();
    if (store.error !== undefined) {
        throw store.error;
    }
    return null;
});

export const StoreProvider = (props: PropsWithChildren<unknown>) => {
    const store = useLocalStore(() => new Store());

    return <storesContext.Provider
        value={store}
    >
        {props.children}
    </storesContext.Provider>;
};
